import { Component, OnInit } from '@angular/core';
import { AuthService } from '../core/auth.service';
import { ApiService } from '../core/api.service';

interface NavItem { label: string; icon: string; link: string; module: string; }

@Component({
  selector: 'app-shell',
  template: `
  <div class="shell" [class.nav-open]="navOpen">
    <aside class="sidebar">
      <div class="side-brand">
        <div class="seal">RB</div>
        <div>
          <div class="side-title">Resume Builder</div>
          <div class="side-sub">{{ auth.user?.orgName }}<span *ngIf="auth.user?.branchName"> · {{ auth.user?.branchName }}</span></div>
        </div>
      </div>

      <nav>
        <a *ngIf="auth.can('DASHBOARD')" routerLink="/dashboard" routerLinkActive="active" (click)="navOpen = false">
          <span class="ico">⌂</span>Dashboard
        </a>

        <!-- My Account: opens its page and expands to show My Profile, My Resumes, Security and Passwords, Logout -->
        <a routerLink="/my-account" routerLinkActive="active" [class.group-open]="accountOpen" (click)="accountOpen = !accountOpen">
          <span class="ico">👤</span>My Account<span class="caret">{{ accountOpen ? '▾' : '▸' }}</span>
        </a>
        <div class="subnav" *ngIf="accountOpen">
          <ng-container *ngFor="let n of accountNav">
            <a *ngIf="auth.can(n.module)" [routerLink]="n.link" routerLinkActive="active" (click)="navOpen = false">
              <span class="ico">{{ n.icon }}</span>{{ n.label }}
            </a>
          </ng-container>
          <a (click)="auth.askLogout()"><span class="ico">⎋</span>Logout</a>
        </div>

        <!-- Administrative Settings: same look as the Resume Builder title block, "AI" in the seal -->
        <div class="side-brand admin-brand" *ngIf="hasAdmin()">
          <div class="seal">AI</div>
          <div>
            <div class="side-title">Administrative Settings</div>
            <div class="side-sub">{{ auth.user?.orgName }}<span *ngIf="auth.user?.branchName"> · {{ auth.user?.branchName }}</span></div>
          </div>
        </div>
        <ng-container *ngFor="let n of adminNav">
          <a *ngIf="auth.can(n.module) && (n.module !== 'ESSENTIAL_SETTINGS' || auth.isSuperAdmin)"
             [routerLink]="n.link" routerLinkActive="active" (click)="navOpen = false">
            <span class="ico">{{ n.icon }}</span>{{ n.label }}
            <span class="pill" *ngIf="n.module === 'USER_MANAGEMENT' && pending">{{ pending }}</span>
          </a>
        </ng-container>

      </nav>
    </aside>

    <div class="main">
      <header class="topbar">
        <button class="hamburger" (click)="navOpen = !navOpen" aria-label="Menu">☰</button>
        <div class="tagline">Build a résumé for any profession — student, athlete, civilian or federal.</div>
        <div class="top-actions">
          <div class="bell" (click)="toggleNotes()" title="Notifications">🔔<span *ngIf="unread" class="badge">{{ unread }}</span></div>
          <div class="who">
            <b>{{ auth.user?.fullName }}</b>
            <small>{{ auth.user?.roleName || 'Member' }}</small>
          </div>
          <button class="btn ghost sm" (click)="auth.askLogout()">Sign Out</button>
        </div>
        <div class="notes" *ngIf="showNotes">
          <div class="notes-head">Notifications <a (click)="readAll()">Mark all read</a></div>
          <div *ngIf="!notes.length" class="muted small pad">Nothing new.</div>
          <div *ngFor="let n of notes" class="note" [class.unread]="!n.isRead">
            {{ n.message }}<small>{{ n.createdAt | appDate:true }}</small>
          </div>
        </div>
      </header>
      <section class="content"><router-outlet></router-outlet></section>
      <footer class="foot">© {{ year }} Resume Builder · Integraff Tech Solution</footer>
    </div>
    <div class="scrim" (click)="navOpen = false"></div>
  </div>`
})
export class ShellComponent implements OnInit {
  navOpen = false;
  pending = 0;
  unread = 0;
  notes: any[] = [];
  showNotes = false;
  year = new Date().getFullYear();

  accountOpen = true;
  accountNav: NavItem[] = [
    { label: 'My Profile', icon: '🪪', link: '/profile', module: 'MY_PROFILE' },
    { label: 'My Resumes', icon: '🎓', link: '/my-resumes', module: 'MY_RESUMES' },
    { label: 'Security and Passwords', icon: '🔒', link: '/security', module: 'CHANGE_PASSWORD' }
  ];
  adminNav: NavItem[] = [
    { label: 'Accounts Review and Activation', icon: '👥', link: '/admin/users', module: 'USER_MANAGEMENT' },
    { label: 'AI Natural Learning', icon: '🏢', link: '/admin/fundamental-settings', module: 'FUNDAMENTAL_SETTINGS' },
    { label: 'Security Credentials', icon: '⚙', link: '/admin/essential-settings', module: 'ESSENTIAL_SETTINGS' }
  ];

  constructor(public auth: AuthService, private api: ApiService) {}

  ngOnInit() {
    // Re-sync permissions / date format in case an admin changed them
    this.auth.refresh().subscribe(s => this.unread = s.unreadNotifications, () => {});
    if (this.auth.can('USER_MANAGEMENT')) {
      this.api.get<number>('/users/pending-count').subscribe(n => this.pending = n, () => {});
    }
  }

  hasAdmin() { return this.adminNav.some(n => this.auth.can(n.module)); }

  toggleNotes() {
    this.showNotes = !this.showNotes;
    if (this.showNotes) { this.api.get<any[]>('/profile/notifications').subscribe(n => this.notes = n); }
  }

  readAll() {
    this.api.post('/profile/notifications/read-all').subscribe(() => {
      this.unread = 0;
      this.notes.forEach(n => n.isRead = true);
    });
  }
}
